import { DEV } from "solid-js"
import Unexpected from "../../../Exception/Unexpected.class"
import { RIDGE_ELEVATION_DIFFERENCE } from "../../../core/entity/EntityElevation/elevation_consts"

function isRidge(a: number, b: number): boolean {
  return Math.abs(a - b) >= RIDGE_ELEVATION_DIFFERENCE
}

let sum : number
let count : number
function reset(): void {
  sum = 0
  count = 0
}
function add(elevation: number, useTile: boolean): void {
  if (useTile) {
    sum += elevation
    count++
  }
}

function avg(): number {
  return count > 0 ? (sum / count) : 0
}

/**
 * Weighted interpolation of elevation at an arbitrary point inside a tile.
 *
 * @param weights   WeightsV2 from calculateNeighborWeights
 * @param elev      ElevationsV2 for base + 8 neighbours
 * @returns         The interpolated elevation
 */
export default function createNodeNeighborAvgElevationListV2(
  base: number,
  list: number[], // length === 8
): number[] {

  if (DEV) {
    if (list.length !== 8) {
      throw new Unexpected('list.length !== 8')
    }
  }

  const [
    northElevation,
    northEastElevation,
    eastElevation,
    southEastElevation,
    southElevation,
    southWestElevation,
    westElevation,
    northWestElevation,
  ] = list

  // Check for ridges between base and each neighbor
  const useNorthTile = !isRidge(base, northElevation)
  const useEastTile = !isRidge(base, eastElevation)
  const useSouthTile = !isRidge(base, southElevation)
  const useWestTile = !isRidge(base, westElevation)

  // Helper for edge averages (null when edge crosses a ridge)
  // function edgeAvgOrNull(a: number, b: number): number | null {
  //   return isRidge(a, b) ? null : (a + b) / 2
  // }

  // Shared corner average based on the four surrounding edge averages.
  // Given the four tiles around a corner in clockwise order: A (base), B, C, D,
  // compute the average of non-ridge edge averages for edges: A-B, B-C, C-D, D-A.
  function sharedCornerAvg(a: number, b: number, c: number, d: number): number {
    // Determine ridge status for each edge around the corner
    const ab_ok = !isRidge(a, b)
    const bc_ok = !isRidge(b, c)
    const cd_ok = !isRidge(c, d)
    const da_ok = !isRidge(d, a)

    // If both edges that touch the base are ridges, this corner should not be
    // influenced by neighbors at all. Fall back to base immediately.
    if (!ab_ok && !da_ok) return base

    // Reachability from base (A) to the other vertices using only non-ridge edges
    // const A_reach = true
    const B_reach = ab_ok
    const D_reach = da_ok
    const C_reach = (B_reach && bc_ok) || (D_reach && cd_ok)

    // Include an edge average only if:
    // 1) the edge itself is not a ridge, and
    // 2) at least one of its endpoints is reachable from the base via non-ridge edges
    const e1 = ab_ok ? (a + b) / 2 : null // A-B
    const e2 = bc_ok && (B_reach || C_reach) ? (b + c) / 2 : null // B-C
    const e3 = cd_ok && (C_reach || D_reach) ? (c + d) / 2 : null // C-D
    const e4 = da_ok ? (d + a) / 2 : null // D-A

    reset()
    add(e1 ?? 0, e1 != null)
    add(e2 ?? 0, e2 != null)
    add(e3 ?? 0, e3 != null)
    add(e4 ?? 0, e4 != null)
    // If nothing qualified (all null), fall back to base
    return count > 0 ? avg() : base
  }

  // For cardinal directions, if there's a ridge, use base value only
  // Otherwise, average the base and the neighbor's elevation
  const northElevationAvg = useNorthTile ? (base + northElevation) / 2 : base
  const eastElevationAvg = useEastTile ? (base + eastElevation) / 2 : base
  const southElevationAvg = useSouthTile ? (base + southElevation) / 2 : base
  const westElevationAvg = useWestTile ? (base + westElevation) / 2 : base

  // Corner averages are shared values derived from surrounding edge averages
  const northWestElevationAvg = sharedCornerAvg(base, northElevation, northWestElevation, westElevation)
  const northEastElevationAvg = sharedCornerAvg(base, northElevation, northEastElevation, eastElevation)
  const southEastElevationAvg = sharedCornerAvg(base, eastElevation, southEastElevation, southElevation)
  const southWestElevationAvg = sharedCornerAvg(base, southElevation, southWestElevation, westElevation)

  const avgs = [
    northElevationAvg,
    northEastElevationAvg,
    eastElevationAvg,
    southEastElevationAvg,
    southElevationAvg,
    southWestElevationAvg,
    westElevationAvg,
    northWestElevationAvg,
  ]
  return avgs
}

